import { useContext, useState } from 'react';
import { Modal, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { AuthContext } from '@/contexts/AuthContext';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { XIcon } from '@/UI/Icons/XIcon';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import PublishToCommunityModalDescription, {
  CATEGORY_OPTIONS,
  DESCRIPTION_MAX,
  NAME_MAX,
  TAGS_MAX,
} from './PublishToCommunityModalDescription';
import PublishToCommunityModalImages from './PublishToCommunityModalImages';
import PublishToCommunityModalDetails from './PublishToCommunityModalDetails';

enum Step {
  Description = 'description',
  Images = 'images',
  Details = 'details',
}

type FormValues = {
  name: string;
  description: string;
  category: string;
  tags: string[];
  // TODO: replace with real upload state once the upload pipeline exists.
  hasThumbnail: boolean;
  supportEmail: string;
  agreedToTerms: boolean;
};

type FormErrors = {
  name: string | null;
  description: string | null;
  image: string | null;
  terms: string | null;
};

interface PublishToCommunityModalProps {
  open: boolean;
  onClose: () => void;
}

const PublishToCommunityModal = ({ open, onClose }: PublishToCommunityModalProps) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const { updateSnackbarData } = useGlobalStore();
  const recipe = useWorkflowStore((state) => state.recipe);

  const [step, setStep] = useState<Step>(Step.Description);
  const [form, setForm] = useState<FormValues>(() => ({
    name: recipe.name.slice(0, NAME_MAX),
    description: '',
    category: CATEGORY_OPTIONS[0].value,
    tags: [],
    // TODO: revert to `false` once real upload exists. Defaulted to `true` so users can advance
    // past the images step while upload is unimplemented; the validateImages gate stays in place.
    hasThumbnail: true,
    supportEmail: currentUser?.email ?? '',
    agreedToTerms: false,
  }));
  const [errors, setErrors] = useState<FormErrors>({
    name: null,
    description: null,
    image: null,
    terms: null,
  });

  const setField = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const clearError = (field: keyof FormErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  };

  const toggleTag = (tag: string) => {
    setForm((prev) => {
      if (prev.tags.includes(tag)) return { ...prev, tags: prev.tags.filter((t) => t !== tag) };
      if (prev.tags.length >= TAGS_MAX) return prev;
      return { ...prev, tags: [...prev.tags, tag] };
    });
  };

  const handleNameChange = (v: string) => {
    setField('name', v.slice(0, NAME_MAX));
    clearError('name');
  };
  const handleDescriptionChange = (v: string) => {
    setField('description', v.slice(0, DESCRIPTION_MAX));
    clearError('description');
  };
  const handleAgreedToTermsChange = (v: boolean) => {
    setField('agreedToTerms', v);
    clearError('terms');
  };
  const handleUploadStub = () => {
    // TODO: replace with real upload flow that flips `hasThumbnail`; for now we only surface a
    // coming-soon snackbar so the validation gate stays honest (Next remains blocked).
    updateSnackbarData({
      text: 'Image uploads are coming soon',
      isOpen: true,
    });
  };
  const handleTermsClick = () => {
    // TODO: link to the real Community Terms of Service URL once it exists.
    updateSnackbarData({
      text: 'Community Terms of Service is coming soon',
      isOpen: true,
    });
  };

  const validateDescription = (): boolean => {
    const next: Partial<FormErrors> = {};
    if (!form.name.trim()) {
      next.name = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.NAME_REQUIRED);
    }
    if (!form.description.trim()) {
      next.description = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.DESCRIPTION_REQUIRED);
    }
    if (Object.keys(next).length === 0) return true;
    setErrors((prev) => ({ ...prev, ...next }));
    return false;
  };

  const validateImages = (): boolean => {
    if (form.hasThumbnail) return true;
    setErrors((prev) => ({
      ...prev,
      image: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.IMAGE_REQUIRED),
    }));
    return false;
  };

  const validateDetails = (): boolean => {
    if (form.agreedToTerms) return true;
    // TODO: i18n this once the final copy for the terms-required error is locked.
    setErrors((prev) => ({ ...prev, terms: 'You must agree to the Community Terms of Service' }));
    return false;
  };

  const handleClose = () => {
    setStep(Step.Description);
    onClose();
  };

  const handleNext = () => {
    if (step === Step.Description) {
      if (!validateDescription()) return;
      setStep(Step.Images);
    } else if (step === Step.Images) {
      if (!validateImages()) return;
      setStep(Step.Details);
    }
  };

  const handlePublish = () => {
    if (!validateDescription()) {
      setStep(Step.Description);
      return;
    }
    if (!validateImages()) {
      setStep(Step.Images);
      return;
    }
    if (!validateDetails()) {
      setStep(Step.Details);
      return;
    }
    // TODO: wire up real publish-to-community API call once the backend endpoint exists
    updateSnackbarData({
      text: 'Publish requested — backend integration pending',
      isOpen: true,
    });
    handleClose();
  };

  const title = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TITLE_PUBLISH);

  return (
    <Modal open={open} onClose={handleClose}>
      <FlexCol
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 648,
          background: color.Black92,
          border: `1px solid ${color.White08_T}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <FlexCenVer
          sx={{
            justifyContent: 'space-between',
            p: 3,
            borderBottom: `1px solid ${color.White16_T}`,
          }}
        >
          <Typography variant="body-lg-md">{title}</Typography>
          <AppIconButton onClick={handleClose}>
            <XIcon width={20} height={20} />
          </AppIconButton>
        </FlexCenVer>

        <Flex sx={{ height: 430 }}>
          <FlexCol
            sx={{
              width: 213,
              p: '24px 18px',
              gap: 0.5,
              borderRight: `1px solid ${color.White16_T}`,
              flexShrink: 0,
            }}
          >
            <SidebarItem
              index={1}
              label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.STEPS.DESCRIPTION)}
              active={step === Step.Description}
              onClick={() => setStep(Step.Description)}
            />
            <SidebarItem
              index={2}
              label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.STEPS.IMAGES)}
              active={step === Step.Images}
              onClick={() => setStep(Step.Images)}
            />
            <SidebarItem
              index={3}
              label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.STEPS.DETAILS)}
              active={step === Step.Details}
              onClick={() => setStep(Step.Details)}
            />
          </FlexCol>

          <FlexCol sx={{ flex: 1, p: 3, gap: 3, overflowY: 'auto' }}>
            {step === Step.Description && (
              <PublishToCommunityModalDescription
                name={form.name}
                onNameChange={handleNameChange}
                nameError={errors.name}
                description={form.description}
                onDescriptionChange={handleDescriptionChange}
                descriptionError={errors.description}
                category={form.category}
                onCategoryChange={(v) => setField('category', v)}
                tags={form.tags}
                onToggleTag={toggleTag}
              />
            )}
            {step === Step.Images && (
              <PublishToCommunityModalImages onUploadClick={handleUploadStub} imageError={errors.image} />
            )}
            {step === Step.Details && (
              <PublishToCommunityModalDetails
                supportEmail={form.supportEmail}
                onSupportEmailChange={(v) => setField('supportEmail', v)}
                agreedToTerms={form.agreedToTerms}
                onAgreedToTermsChange={handleAgreedToTermsChange}
                termsError={errors.terms}
                onTermsClick={handleTermsClick}
              />
            )}
          </FlexCol>
        </Flex>

        <FlexCenVer
          sx={{
            justifyContent: 'flex-end',
            gap: 1,
            p: 3,
            borderTop: `1px solid ${color.White16_T}`,
          }}
        >
          <ButtonContained mode="text" size="medium" onClick={handleClose}>
            {t(I18N_KEYS.GENERAL.CANCEL)}
          </ButtonContained>
          {step === Step.Details ? (
            <ButtonContained mode="filled-light" size="medium" onClick={handlePublish}>
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.PUBLISH)}
            </ButtonContained>
          ) : (
            <ButtonContained mode="filled-light" size="medium" onClick={handleNext}>
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.NEXT)}
            </ButtonContained>
          )}
        </FlexCenVer>
      </FlexCol>
    </Modal>
  );
};

const SidebarItem = ({
  index,
  label,
  active,
  onClick,
}: {
  index: number;
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <FlexCenVer
    onClick={onClick}
    sx={{
      gap: 1,
      p: 1,
      borderRadius: 1,
      cursor: 'pointer',
      background: active ? color.Yellow8_T : 'transparent',
      '&:hover': { background: active ? color.Yellow8_T : color.White04_T },
    }}
  >
    <FlexCenVer
      sx={{
        width: 16,
        height: 16,
        borderRadius: '3px',
        justifyContent: 'center',
        background: active ? color.Yellow100 : color.White04_T,
        color: active ? color.Black92_T : color.White100,
        fontSize: 9,
        fontWeight: 550,
        fontFamily: 'Inter, sans-serif',
        flexShrink: 0,
      }}
    >
      {index}
    </FlexCenVer>
    <Typography variant="body-sm-rg">{label}</Typography>
  </FlexCenVer>
);

export default PublishToCommunityModal;
